import { useState, useEffect } from 'react';
import { dashboardAPI, exportAPI, downloadBlob } from '../lib/api';
import { Button } from '../components/ui/button';
import { Card, CardContent, CardHeader, CardTitle } from '../components/ui/card';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '../components/ui/select';
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuTrigger,
} from '../components/ui/dropdown-menu';
import { toast } from 'sonner';
import {
  Download,
  Trophy,
  Package,
  ShoppingCart,
  TrendingUp,
  FileSpreadsheet,
  FileText,
  Loader2,
  Store,
  Medal
} from 'lucide-react';
import {
  BarChart,
  Bar,
  XAxis,
  YAxis,
  CartesianGrid,
  Tooltip,
  ResponsiveContainer,
  LineChart,
  Line,
  PieChart,
  Pie,
  Cell
} from 'recharts';

const MONTHS_LIST = [
  { value: 1, label: 'Janeiro' },
  { value: 2, label: 'Fevereiro' },
  { value: 3, label: 'Março' },
  { value: 4, label: 'Abril' },
  { value: 5, label: 'Maio' },
  { value: 6, label: 'Junho' },
  { value: 7, label: 'Julho' },
  { value: 8, label: 'Agosto' },
  { value: 9, label: 'Setembro' },
  { value: 10, label: 'Outubro' },
  { value: 11, label: 'Novembro' },
  { value: 12, label: 'Dezembro' }
];

const MONTHS_SHORT = ['Jan', 'Fev', 'Mar', 'Abr', 'Mai', 'Jun', 'Jul', 'Ago', 'Set', 'Out', 'Nov', 'Dez'];

const CHART_COLORS = ['#1e3a8a', '#3b82f6', '#10b981', '#f59e0b', '#ef4444'];

export const Reports = () => {
  const [loading, setLoading] = useState(true);
  const [stats, setStats] = useState(null);
  const [storesRanking, setStoresRanking] = useState([]);
  const [productsRanking, setProductsRanking] = useState([]);
  const [monthlyOrders, setMonthlyOrders] = useState([]);
  const [selectedYear, setSelectedYear] = useState(new Date().getFullYear().toString());

  const currentYear = new Date().getFullYear();
  const years = Array.from({ length: 5 }, (_, i) => currentYear - i);

  useEffect(() => {
    loadData();
  }, [selectedYear]);

  const loadData = async () => {
    setLoading(true);
    try {
      const [statsRes, storesRes, productsRes, monthlyRes] = await Promise.all([
        dashboardAPI.getStats(),
        dashboardAPI.getStoresRanking({ limit: 10 }),
        dashboardAPI.getProductsRanking({ limit: 10 }),
        dashboardAPI.getMonthlyOrders({ year: parseInt(selectedYear) })
      ]);

      setStats(statsRes.data);
      setStoresRanking(storesRes.data);
      setProductsRanking(productsRes.data);

      // Format monthly data
      const monthlyData = Object.entries(monthlyRes.data).map(([month, data]) => ({
        name: MONTHS_SHORT[parseInt(month) - 1],
        pedidos: data.count,
        receita: data.revenue,
        confirmados: data.confirmed,
        cancelados: data.cancelled
      }));
      setMonthlyOrders(monthlyData);
    } catch (error) {
      toast.error('Erro ao carregar relatórios');
    } finally {
      setLoading(false);
    }
  };

  const handleExportOrders = async (format) => {
    try {
      const { data } = await exportAPI.orders({ format, year: parseInt(selectedYear) });
      downloadBlob(data, `pedidos-${selectedYear}.${format}`);
      toast.success('Exportação concluída!');
    } catch (error) {
      toast.error('Erro ao exportar');
    }
  };

  const formatCurrency = (value) => {
    return new Intl.NumberFormat('pt-BR', {
      style: 'currency',
      currency: 'BRL'
    }).format(value || 0);
  };

  const getMedalColor = (index) => {
    if (index === 0) return 'text-yellow-500';
    if (index === 1) return 'text-slate-400';
    if (index === 2) return 'text-amber-700';
    return 'text-slate-300';
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center h-64">
        <Loader2 className="w-8 h-8 animate-spin text-blue-600" />
      </div>
    );
  }

  const orderStatusData = [
    { name: 'Confirmados', value: stats?.confirmed_orders || 0, color: '#10b981' },
    { name: 'Pendentes', value: stats?.pending_orders || 0, color: '#f59e0b' },
    { name: 'Cancelados', value: stats?.cancelled_orders || 0, color: '#ef4444' }
  ].filter(item => item.value > 0);

  return (
    <div className="space-y-6" data-testid="reports-page">
      {/* Header */}
      <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-4">
        <div>
          <h1 className="text-3xl font-bold text-slate-900 tracking-tight">Relatórios</h1>
          <p className="text-slate-500 mt-1">Rankings e estatísticas de vendas</p>
        </div>
        <div className="flex gap-2">
          <Select value={selectedYear} onValueChange={setSelectedYear}>
            <SelectTrigger className="w-28" data-testid="year-filter">
              <SelectValue />
            </SelectTrigger>
            <SelectContent>
              {years.map((year) => (
                <SelectItem key={year} value={year.toString()}>{year}</SelectItem>
              ))}
            </SelectContent>
          </Select>
          <DropdownMenu>
            <DropdownMenuTrigger asChild>
              <Button variant="outline" data-testid="export-btn">
                <Download className="w-4 h-4 mr-2" />
                Exportar
              </Button>
            </DropdownMenuTrigger>
            <DropdownMenuContent>
              <DropdownMenuItem onClick={() => handleExportOrders('xlsx')}>
                <FileSpreadsheet className="w-4 h-4 mr-2" />
                Pedidos Excel (.xlsx)
              </DropdownMenuItem>
              <DropdownMenuItem onClick={() => handleExportOrders('csv')}>
                <FileText className="w-4 h-4 mr-2" />
                Pedidos CSV
              </DropdownMenuItem>
            </DropdownMenuContent>
          </DropdownMenu>
        </div>
      </div>

      {/* Summary Cards */}
      <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-6">
        <Card className="border-slate-200 border-l-4 border-l-blue-600">
          <CardContent className="p-6">
            <div className="flex items-center justify-between">
              <div>
                <p className="text-sm text-slate-500">Total de Pedidos</p>
                <p className="text-3xl font-bold text-slate-900 mt-1">{stats?.total_orders || 0}</p>
              </div>
              <ShoppingCart className="w-8 h-8 text-blue-600" />
            </div>
          </CardContent>
        </Card>

        <Card className="border-slate-200 border-l-4 border-l-green-600">
          <CardContent className="p-6">
            <div className="flex items-center justify-between">
              <div>
                <p className="text-sm text-slate-500">Confirmados</p>
                <p className="text-3xl font-bold text-green-600 mt-1">{stats?.confirmed_orders || 0}</p>
              </div>
              <TrendingUp className="w-8 h-8 text-green-600" />
            </div>
          </CardContent>
        </Card>

        <Card className="border-slate-200 border-l-4 border-l-amber-600">
          <CardContent className="p-6">
            <div className="flex items-center justify-between">
              <div>
                <p className="text-sm text-slate-500">Pendentes</p>
                <p className="text-3xl font-bold text-amber-600 mt-1">{stats?.pending_orders || 0}</p>
              </div>
              <Package className="w-8 h-8 text-amber-600" />
            </div>
          </CardContent>
        </Card>

        <Card className="border-slate-200 border-l-4 border-l-red-600">
          <CardContent className="p-6">
            <div className="flex items-center justify-between">
              <div>
                <p className="text-sm text-slate-500">Cancelados</p>
                <p className="text-3xl font-bold text-red-600 mt-1">{stats?.cancelled_orders || 0}</p>
              </div>
              <ShoppingCart className="w-8 h-8 text-red-600" />
            </div>
          </CardContent>
        </Card>
      </div>

      {/* Charts Row */}
      <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
        {/* Monthly Orders Chart */}
        <Card className="border-slate-200" data-testid="monthly-orders-chart">
          <CardHeader>
            <CardTitle className="text-lg font-semibold text-slate-900">
              Pedidos por Mês ({selectedYear})
            </CardTitle>
          </CardHeader>
          <CardContent>
            <div className="h-80">
              <ResponsiveContainer width="100%" height="100%">
                <BarChart data={monthlyOrders}>
                  <CartesianGrid strokeDasharray="3 3" stroke="#e2e8f0" />
                  <XAxis dataKey="name" tick={{ fill: '#64748b', fontSize: 12 }} />
                  <YAxis tick={{ fill: '#64748b', fontSize: 12 }} />
                  <Tooltip
                    contentStyle={{
                      backgroundColor: '#0f172a',
                      border: 'none',
                      borderRadius: '8px',
                      color: 'white'
                    }}
                  />
                  <Bar dataKey="confirmados" fill="#10b981" name="Confirmados" stackId="a" />
                  <Bar dataKey="cancelados" fill="#ef4444" name="Cancelados" stackId="a" />
                </BarChart>
              </ResponsiveContainer>
            </div>
          </CardContent>
        </Card>

        {/* Order Status Pie Chart */}
        <Card className="border-slate-200" data-testid="order-status-chart">
          <CardHeader>
            <CardTitle className="text-lg font-semibold text-slate-900">
              Status dos Pedidos
            </CardTitle>
          </CardHeader>
          <CardContent>
            <div className="h-80">
              {orderStatusData.length > 0 ? (
                <ResponsiveContainer width="100%" height="100%">
                  <PieChart>
                    <Pie
                      data={orderStatusData}
                      cx="50%"
                      cy="50%"
                      innerRadius={60}
                      outerRadius={100}
                      paddingAngle={5}
                      dataKey="value"
                      label={({ name, percent }) => `${name} ${(percent * 100).toFixed(0)}%`}
                    >
                      {orderStatusData.map((entry, index) => (
                        <Cell key={`cell-${index}`} fill={entry.color} />
                      ))}
                    </Pie>
                    <Tooltip
                      contentStyle={{
                        backgroundColor: '#0f172a',
                        border: 'none',
                        borderRadius: '8px',
                        color: 'white'
                      }}
                    />
                  </PieChart>
                </ResponsiveContainer>
              ) : (
                <div className="h-full flex items-center justify-center text-slate-400">
                  Sem dados de pedidos
                </div>
              )}
            </div>
            <div className="flex justify-center gap-6 mt-4">
              {orderStatusData.map((item, index) => (
                <div key={index} className="flex items-center gap-2 text-sm">
                  <div className="w-3 h-3 rounded-full" style={{ backgroundColor: item.color }} />
                  <span className="text-slate-600">{item.name}: {item.value}</span>
                </div>
              ))}
            </div>
          </CardContent>
        </Card>
      </div>

      {/* Revenue Chart */}
      <Card className="border-slate-200" data-testid="revenue-chart">
        <CardHeader>
          <CardTitle className="text-lg font-semibold text-slate-900">
            Receita Mensal ({selectedYear})
          </CardTitle>
        </CardHeader>
        <CardContent>
          <div className="h-80">
            <ResponsiveContainer width="100%" height="100%">
              <LineChart data={monthlyOrders}>
                <CartesianGrid strokeDasharray="3 3" stroke="#e2e8f0" />
                <XAxis dataKey="name" tick={{ fill: '#64748b', fontSize: 12 }} />
                <YAxis tick={{ fill: '#64748b', fontSize: 12 }} tickFormatter={(v) => `R$${v}`} />
                <Tooltip
                  contentStyle={{
                    backgroundColor: '#0f172a',
                    border: 'none',
                    borderRadius: '8px',
                    color: 'white'
                  }}
                  formatter={(value) => [formatCurrency(value), 'Receita']}
                />
                <Line
                  type="monotone"
                  dataKey="receita"
                  stroke="#3b82f6"
                  strokeWidth={3}
                  dot={{ fill: '#3b82f6', strokeWidth: 2 }}
                />
              </LineChart>
            </ResponsiveContainer>
          </div>
        </CardContent>
      </Card>

      {/* Rankings */}
      <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
        {/* Stores Ranking */}
        <Card className="border-slate-200" data-testid="stores-ranking">
          <CardHeader>
            <CardTitle className="text-lg font-semibold text-slate-900 flex items-center gap-2">
              <Trophy className="w-5 h-5 text-yellow-500" />
              Ranking de Lojas
            </CardTitle>
          </CardHeader>
          <CardContent>
            {storesRanking.length === 0 ? (
              <div className="text-center py-8 text-slate-400">
                <Store className="w-12 h-12 mx-auto mb-2" />
                <p>Nenhuma loja cadastrada</p>
              </div>
            ) : (
              <div className="space-y-3">
                {storesRanking.map((store, index) => (
                  <div
                    key={store.id}
                    className="flex items-center gap-4 p-3 bg-slate-50 rounded-lg hover:bg-slate-100 transition-colors"
                    data-testid={`store-rank-${index + 1}`}
                  >
                    <div className="flex items-center justify-center w-8">
                      {index < 3 ? (
                        <Medal className={`w-6 h-6 ${getMedalColor(index)}`} />
                      ) : (
                        <span className="text-slate-400 font-bold">{index + 1}</span>
                      )}
                    </div>
                    <div className="flex-1 min-w-0">
                      <p className="font-medium text-slate-900 truncate">{store.name}</p>
                      <p className="text-sm text-slate-500">{store.city}/{store.state}</p>
                    </div>
                    <div className="text-right">
                      <p className="font-bold text-green-600">{formatCurrency(store.total_revenue)}</p>
                      <p className="text-sm text-slate-500">{store.total_orders || 0} pedidos</p>
                    </div>
                  </div>
                ))}
              </div>
            )}
          </CardContent>
        </Card>

        {/* Products Ranking */}
        <Card className="border-slate-200" data-testid="products-ranking">
          <CardHeader>
            <CardTitle className="text-lg font-semibold text-slate-900 flex items-center gap-2">
              <Package className="w-5 h-5 text-blue-500" />
              Produtos Mais Vendidos
            </CardTitle>
          </CardHeader>
          <CardContent>
            {productsRanking.length === 0 ? (
              <div className="text-center py-8 text-slate-400">
                <Package className="w-12 h-12 mx-auto mb-2" />
                <p>Nenhum produto cadastrado</p>
              </div>
            ) : (
              <div className="space-y-3">
                {productsRanking.map((product, index) => (
                  <div
                    key={product.id}
                    className="flex items-center gap-4 p-3 bg-slate-50 rounded-lg hover:bg-slate-100 transition-colors"
                    data-testid={`product-rank-${index + 1}`}
                  >
                    <div className="flex items-center justify-center w-8">
                      {index < 3 ? (
                        <Medal className={`w-6 h-6 ${getMedalColor(index)}`} />
                      ) : (
                        <span className="text-slate-400 font-bold">{index + 1}</span>
                      )}
                    </div>
                    <div className="flex-1 min-w-0">
                      <p className="font-medium text-slate-900 truncate">{product.name}</p>
                      <p className="text-sm text-slate-500">{product.category || 'Sem categoria'}</p>
                    </div>
                    <div className="text-right">
                      <p className="font-bold text-blue-600">{product.total_sold || 0}</p>
                      <p className="text-sm text-slate-500">vendidos</p>
                    </div>
                  </div>
                ))}
              </div>
            )}
          </CardContent>
        </Card>
      </div>
    </div>
  );
};
